import { FC, useRef, useState } from 'react';
import { cn } from '@repo/ui/lib/utils';
import { ArrowLeft, ArrowRight, X } from 'lucide-react';
import { VisuallyHidden } from '@radix-ui/react-visually-hidden';

import { Button } from '@repo/ui/components/ui/button';
import { LoadingMedia } from '../../../../../../../../apps/web/src/components/loading/default';
import {
  ReactZoomPanPinchRef,
  TransformComponent,
  TransformWrapper,
} from 'react-zoom-pan-pinch';
import {
  type CandidateMedia,
  CandidateMediaType,
  spotlightMediaTypes,
} from '@repo/ui/hooks';
import {
  SpotlightMedia,
  type SpotlightMediaReadyState,
} from '../spotlight-media/component';
import { MobileZoom } from './mobile-zoom/component';
import {
  Sheet,
  SheetDescription,
  SheetHeader,
  SheetOverlay,
  SheetPortal,
  SheetPrimitive,
  SheetTitle,
  sheetVariants,
} from '@repo/ui/components/ui/sheet';

export const Lightbox: FC<{
  open?: boolean;
  isVideoMuted: boolean;
  videoAutoplay: boolean;
  imageMediaAlt?: string;
  currentMedia: CandidateMedia;
  nextMedia: CandidateMedia;
  previewImages: CandidateMedia[];
  ariaDescription?: string;
  onArrowLeftClick?: () => void;
  onArrowRightClick?: () => void;
  onOpenChange?: (open: boolean) => void;
  onMediaSwipeLeft?: () => void;
  onMediaSwipeRight?: () => void;
  onVolumeButtonClick?: () => void;
  onImageAnimationEnd?: () => void;
  onPreviewImageClick?: (media: CandidateMedia) => void;
  onSpotlightMediaReady?: (data: SpotlightMediaReadyState) => void;
  showNavigationControls?: boolean;
}> = ({
  open,
  nextMedia,
  videoAutoplay,
  imageMediaAlt,
  isVideoMuted,
  currentMedia,
  ariaDescription = 'Media lightbox gallery view',
  previewImages,
  onOpenChange,
  onMediaSwipeLeft,
  onMediaSwipeRight,
  onArrowLeftClick,
  onArrowRightClick,
  onVolumeButtonClick,
  onImageAnimationEnd,
  onPreviewImageClick,
  onSpotlightMediaReady,
  showNavigationControls = true,
}) => {
  const [firstPreviewImage, secondPreviewImage] = previewImages;
  const transformComponentRef = useRef<ReactZoomPanPinchRef | null>(null);
  const [enableMobileZoom, setEnableMobileZoom] = useState(false);
  const isNextPreviewImageLandscape =
    CandidateMediaType.runway === firstPreviewImage?.metadata?.type;
  const hasPreviewImages = previewImages.length > 0;
  const showMediaNavigation = showNavigationControls && hasPreviewImages;

  return (
    <>
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetPortal>
          <SheetOverlay className="bg-black/80 backdrop-blur-2xl" />
          <SheetPrimitive.Content
            className={cn(
              sheetVariants({ side: 'right' }),
              'w-full sm:max-w-[calc(100%-84px)] lg:max-w-[calc(100%-234px)] xl:max-w-[calc(100%-334px)] p-0 bg-transparent border-l-0 shadow-none mt-13 max-h-151.75',
            )}
          >
            <SheetHeader>
              <VisuallyHidden>
                <SheetDescription>{ariaDescription}</SheetDescription>
              </VisuallyHidden>
              <SheetPrimitive.Close asChild>
                <section className="fixed top-1.5 right-4">
                  <button className="flex items-center justify-center w-10 h-10 text-white">
                    <X className="w-4 h-4" />
                  </button>
                </section>
              </SheetPrimitive.Close>

              <SheetTitle className="fixed top-3 left-4 text-white text-base font-medium">
                Portfolio
              </SheetTitle>
            </SheetHeader>
            {showMediaNavigation && (
              <section>
                <section className="fixed bottom-4 sm:top-89.5 left-4 z-50">
                  <Button
                    className="rounded-full w-8 h-8 bg-transparent hover:bg-transparent text-white hover:text-white p-2.5 border-white/70"
                    variant="outline"
                    data-testid="arrow-left-button"
                    onClick={onArrowLeftClick}
                  >
                    <ArrowLeft className="w-4 h-4" />
                  </Button>
                </section>
                <section className="fixed bottom-4 sm:top-89.5 right-4 z-50">
                  <Button
                    className="rounded-full w-8 h-8 bg-transparent hover:bg-transparent text-white hover:text-white p-2.5 border-white/70"
                    variant="outline"
                    data-testid="arrow-right-button"
                    onClick={onArrowRightClick}
                  >
                    <ArrowRight className="w-4 h-4" />
                  </Button>
                </section>
              </section>
            )}
            <section className="flex flex-row gap-2 w-full h-full justify-center md:justify-normal">
              <section className="w-101.25 min-w-101.25 h-full relative">
                {currentMedia?.isProcessing ? (
                  <LoadingMedia type="spinner" position="center" />
                ) : (
                  <TransformWrapper
                    ref={transformComponentRef}
                    onPinchingStart={() => setEnableMobileZoom(true)}
                    onPinchingStop={() =>
                      transformComponentRef?.current?.resetTransform()
                    }
                    pinch={{ step: 0 }}
                    panning={{ disabled: true }}
                    doubleClick={{ disabled: true }}
                    alignmentAnimation={{ disabled: true }}
                    zoomAnimation={{ disabled: true }}
                    wheel={{ disabled: true }}
                    velocityAnimation={{ disabled: true }}
                  >
                    <TransformComponent
                      wrapperStyle={{
                        width: '100%',
                        height: '100%',
                      }}
                      contentStyle={{ width: '100%', height: '100%' }}
                    >
                      <SpotlightMedia
                        showVolumeVideoControl={true}
                        media={currentMedia}
                        nextMedia={nextMedia}
                        imageMediaAlt={imageMediaAlt}
                        isVideoMuted={isVideoMuted}
                        showPlayPauseVideoControl={true}
                        enableDesktopZoom={true}
                        videoAutoplay={videoAutoplay}
                        onMediaDoubleClick={() => setEnableMobileZoom(true)}
                        onMediaReady={onSpotlightMediaReady}
                        onMediaSwipeRight={onMediaSwipeRight}
                        onMediaSwipeLeft={onMediaSwipeLeft}
                        onVolumeButtonClick={onVolumeButtonClick}
                        onImageAnimationEnd={onImageAnimationEnd}
                        showNavigationControls={false}
                      />
                    </TransformComponent>
                  </TransformWrapper>
                )}
              </section>
              {firstPreviewImage && (
                <section className="hidden bg-transparent h-full grow md:flex flex-row flex-nowrap overflow-hidden gap-2">
                  <section
                    className={cn('h-full', {
                      'w-101.25 min-w-101.25': !isNextPreviewImageLandscape,
                      'w-full': isNextPreviewImageLandscape,
                    })}
                  >
                    <img
                      key={firstPreviewImage.id}
                      onClick={() => onPreviewImageClick?.(firstPreviewImage)}
                      className="w-full h-full object-contain cursor-pointer brightness-[.6] blur-sm opacity-70"
                      src={
                        firstPreviewImage.type === spotlightMediaTypes.image
                          ? firstPreviewImage.url
                          : firstPreviewImage.thumbnailUrl
                      }
                      alt={imageMediaAlt}
                      data-testid={`preview-image-${firstPreviewImage.id}`}
                    />
                  </section>

                  {!isNextPreviewImageLandscape && secondPreviewImage && (
                    <section className="w-full h-full">
                      <img
                        key={secondPreviewImage.id}
                        onClick={() =>
                          onPreviewImageClick?.(secondPreviewImage)
                        }
                        className="w-full h-full object-contain cursor-pointer brightness-[.6] blur-sm opacity-70"
                        src={
                          secondPreviewImage.type === spotlightMediaTypes.image
                            ? secondPreviewImage.url
                            : secondPreviewImage.thumbnailUrl
                        }
                        alt={imageMediaAlt}
                        data-testid={`preview-image-${secondPreviewImage.id}`}
                      />
                    </section>
                  )}
                </section>
              )}
            </section>
          </SheetPrimitive.Content>
        </SheetPortal>
      </Sheet>

      <MobileZoom
        open={enableMobileZoom}
        media={currentMedia}
        mediaAlt={imageMediaAlt}
        onOpenChange={setEnableMobileZoom}
      />
    </>
  );
};
